import React, { useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Divider,
} from '@mui/material';
import { useFormularioSecretariaMunicipalAgriculturaStore } from 'stores/forms/formularioAgricultura.store';
import { KeyboardArrowDownOutlined, KeyboardArrowUpOutlined } from '@mui/icons-material';

const converterChaveValor = {
  'lei_municipal': 'Lei Municipal',
  'decreto': 'Decreto',
  'portaria': 'Portaria',
  'resolucao': 'Resolução',
  'instrucao_normativa': 'Instrução Normativa',
  'outros': 'Outros'
};

const InfoField: React.FC<{ label: string; value: any }> = ({ label, value }) => (
  <Box mb={2}>
    <Typography
      sx={{
        fontWeight: 400,
        fontSize: "14px",
        lineHeight: "143%",
        letterSpacing: "0.17px",
        color: "text.secondary",
        mb: 0.5,
      }}
      gutterBottom
    >
      {label}
    </Typography>
    {value && value !== '' && value !== 'Não informado' ? (
      <Typography
        sx={{
          fontWeight: 400,
          fontSize: "16px",
          lineHeight: "24px",
          letterSpacing: "0.15px",
          color: "text.primary",
        }}
      >
        {value}
      </Typography>
    ) : (
      <Typography
        sx={{
          fontWeight: 400,
          fontSize: "16px",
          lineHeight: "24px",
          letterSpacing: "0.15px",
          color: "text.primary",
        }}
      >
        Não informado
      </Typography>
    )}
  </Box>
);

const Step2View: React.FC = () => {
  const { formulario } = useFormularioSecretariaMunicipalAgriculturaStore();
  const [open, setOpen] = useState<boolean>(true);

  if (!formulario) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <Typography>Carregando informações...</Typography>
      </Box>
    );
  }

  const formatCurrency = (value: number | null) => {
    if (!value) return null;
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return null;
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return null;

      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();

      return `${day}/${month}/${year}`;
    } catch {
      return null;
    }
  };

  return (
    <Paper elevation={1} sx={{ p: 0, mt: 2 }}>
      <Box
        sx={{ px: 3, pt: 2, cursor: 'pointer', userSelect: 'none' }}
        onClick={() => setOpen(!open)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 500,
              color: "#000000DE",
              fontSize: "20px",
              mb: 1,
              display: "flex",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            {open ? (
              <KeyboardArrowUpOutlined
                sx={{ color: "text.secondary", fontSize: 20 }}
              />
            ) : (
              <KeyboardArrowDownOutlined
                sx={{ color: "text.secondary", fontSize: 20 }}
              />
            )}
            Informações Complementares
          </Typography>
        </Box>
        <Divider sx={{ mb: 1 }} />
      </Box>

      {open && (
        <Box sx={{ p: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography
                gutterBottom
                sx={{
                  fontWeight: 700,
                  fontSize: '18px',
                  lineHeight: '160%',
                  letterSpacing: '0.15px',
                }}
              >
                Fundo Municipal de Agricultura Familiar
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <InfoField
                label="Possui Fundo Municipal de Agricultura Familiar"
                value={formulario.fundo_municipal_agricultura_familiar ? 'Sim' : 'Não'}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <InfoField
                label="Tipo de Publicação"
                value={converterChaveValor[formulario.tipo_publicacao as keyof typeof converterChaveValor] || null}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <InfoField
                label="Número da Publicação"
                value={formulario.nro_publicacao ?? null}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <InfoField
                label="Data da Publicação"
                value={formatDate(formulario.data_publicacao)}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography
                gutterBottom
                sx={{
                  fontWeight: 700,
                  fontSize: '18px',
                  lineHeight: '160%',
                  letterSpacing: '0.15px',
                }}
              >
                Instrumento Legal para Manutenção Agrícola
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <InfoField
                label="Possui Instrumento Legal para Manutenção Agrícola"
                value={formulario.instrumento_legal_manutencao_agricola ? 'Sim' : 'Não'}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <InfoField
                label="Tipo de Publicação do Instrumento Legal"
                value={converterChaveValor[formulario.tipo_publicacao_instrumento_legal_manutencao_agricola as keyof typeof converterChaveValor] || null}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <InfoField
                label="Número da Publicação do Instrumento Legal"
                value={formulario.numero_publicacao_instrumento_legal_manutencao_agricola ?? null}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <InfoField
                label="Data da Publicação do Instrumento Legal"
                value={formatDate(formulario.data_publicacao_instrumento_legal_manutencao_agricola)}
              />
            </Grid>
          </Grid>
        </Box>
      )}
    </Paper>
  );
};

export default Step2View;
